import { createContext, useEffect, useState } from 'react';
import { api, GetToken } from '../services/api';

export const ThemeContext = createContext(null);

const THEME_ID_ALIASES = {
    'escuro-profundo': 'deep_dark',
    escuro: 'dark',
    claro: 'light',
    'branco-gelo': 'ice_white',
};

const REVERSE_THEME_ID_ALIASES = {
    deep_dark: 'escuro-profundo',
    dark: 'escuro',
    light: 'claro',
    ice_white: 'branco-gelo',
};

export const PREDEFINED_THEMES = {
    'escuro-profundo': {
        id: 'escuro-profundo',
        backend_id: 'deep_dark',
        name: 'Escuro Profundo',
        description: 'Preto puro para maximo contraste',
        color_primary: '#000000',
        color_secondary: '#1a1a1a',
        color_accent: '#ffffff',
        icon_color: '#ffffff',
        text_primary: '#ffffff',
        text_secondary: '#cccccc',
    },
    escuro: {
        id: 'escuro',
        backend_id: 'dark',
        name: 'Escuro',
        description: 'Cinza escuro confortavel',
        color_primary: '#333333',
        color_secondary: '#4a4a4a',
        color_accent: '#ffffff',
        icon_color: '#ffffff',
        text_primary: '#ffffff',
        text_secondary: '#cccccc',
    },
    claro: {
        id: 'claro',
        backend_id: 'light',
        name: 'Claro',
        description: 'Branco limpo e minimalista',
        color_primary: '#ffffff',
        color_secondary: '#f5f5f5',
        color_accent: '#000000',
        icon_color: '#000000',
        text_primary: '#000000',
        text_secondary: '#666666',
    },
    'branco-gelo': {
        id: 'branco-gelo',
        backend_id: 'ice_white',
        name: 'Branco Gelo',
        description: 'Branco azulado suave',
        color_primary: '#f0f8ff',
        color_secondary: '#e6f2ff',
        color_accent: '#2c3e50',
        icon_color: '#2c3e50',
        text_primary: '#2c3e50',
        text_secondary: '#5a6c7d',
    },
};

function normalizeFrontendThemeId(themeId, { fallback = 'escuro-profundo' } = {}) {
    const raw = String(themeId || '')
        .trim()
        .toLowerCase();
    if (!raw) {
        return fallback;
    }

    if (PREDEFINED_THEMES[raw]) {
        return raw;
    }

    return REVERSE_THEME_ID_ALIASES[raw] || fallback;
}

function normalizeBackendThemeId(themeId) {
    const raw = String(themeId || '')
        .trim()
        .toLowerCase();
    if (!raw) {
        return '';
    }

    return THEME_ID_ALIASES[raw] || raw;
}

function mapBackendTheme(theme = {}) {
    const frontendId = normalizeFrontendThemeId(theme.id, { fallback: null });
    if (!frontendId) {
        return null;
    }

    const fallback = PREDEFINED_THEMES[frontendId] || PREDEFINED_THEMES['escuro-profundo'];
    return {
        id: frontendId,
        backend_id: normalizeBackendThemeId(theme.id || fallback.backend_id),
        name: theme.name || fallback.name,
        description: theme.description || fallback.description,
        color_primary: theme.primary_color || fallback.color_primary,
        color_secondary: theme.secondary_color || fallback.color_secondary,
        color_accent: theme.accent_color || fallback.color_accent,
        icon_color: theme.icon_color || fallback.icon_color,
        text_primary: theme.primary_text || fallback.text_primary,
        text_secondary: theme.secondary_text || fallback.text_secondary,
    };
}

function indexMappedThemes(themes = []) {
    return themes.reduce((acc, theme) => {
        const mapped = mapBackendTheme(theme);
        if (mapped) {
            acc[mapped.id] = mapped;
        }
        return acc;
    }, {});
}

function resolveTheme(themes, themeId) {
    const normalizedThemeId = normalizeFrontendThemeId(themeId);
    return themes[normalizedThemeId] || PREDEFINED_THEMES[normalizedThemeId] || PREDEFINED_THEMES['escuro-profundo'];
}

export const ThemeProvider = ({ children }) => {
    const [theme, setThemeState] = useState(() => normalizeFrontendThemeId(localStorage.getItem('theme')));
    const [effectiveTheme, setEffectiveTheme] = useState('dark');
    const [predefinedThemes, setPredefinedThemes] = useState(PREDEFINED_THEMES);

    const applyTheme = (themeData) => {
        const root = document.documentElement;
        root.setAttribute('data-theme', themeData.id);

        root.style.setProperty('--color-primary', themeData.color_primary);
        root.style.setProperty('--color-secondary', themeData.color_secondary);
        root.style.setProperty('--color-accent', themeData.color_accent);
        root.style.setProperty('--icon-color', themeData.icon_color);
        root.style.setProperty('--text-primary', themeData.text_primary);
        root.style.setProperty('--text-secondary', themeData.text_secondary);

        const isLightTheme = themeData.id === 'claro' || themeData.id === 'branco-gelo';
        if (isLightTheme) {
            root.classList.remove('dark');
            setEffectiveTheme('light');
        } else {
            root.classList.add('dark');
            setEffectiveTheme('dark');
        }

        setTimeout(() => {
            window.dispatchEvent(new Event('themechange'));
        }, 100);
    };

    useEffect(() => {
        applyTheme(resolveTheme(predefinedThemes, theme));
    }, [predefinedThemes, theme]);

    useEffect(() => {
        const syncThemeFromBackend = async () => {
            if (!GetToken()) {
                return;
            }

            try {
                const [backendThemes, backendUserThemes] = await Promise.all([
                    api.getPredefinedThemes(),
                    api.getUserThemes(),
                ]);

                const mappedThemes = indexMappedThemes(Array.isArray(backendThemes) ? backendThemes : []);
                if (Object.keys(mappedThemes).length > 0) {
                    setPredefinedThemes((previous) => ({
                        ...previous,
                        ...mappedThemes,
                    }));
                }

                const activeTheme = Array.isArray(backendUserThemes)
                    ? backendUserThemes.find((entry) => Boolean(entry?.active))
                    : null;
                const normalizedTheme = normalizeFrontendThemeId(activeTheme?.id, { fallback: null });

                if (normalizedTheme) {
                    setThemeState(normalizedTheme);
                    localStorage.setItem('theme', normalizedTheme);
                }
            } catch {
                // Silent fallback: local selection remains valid when backend is unavailable.
            }
        };

        syncThemeFromBackend();
    }, []);

    const setTheme = (newTheme) => {
        const resolvedTheme = normalizeFrontendThemeId(newTheme);
        setThemeState(resolvedTheme);
        localStorage.setItem('theme', resolvedTheme);
    };

    const value = {
        theme,
        effectiveTheme,
        predefinedThemes,
        setTheme,
        selectPredefinedTheme: setTheme,
        applyTheme,
    };

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
